<template>
  <div
    class="listing-toolbar-collapse"
    :class="{ 'is-collapsed': collapsed, 'is-expanding': expanding }"
    @transitionend.self="onTransitionEnd"
  >
    <div class="listing-toolbar" :class="{ 'listing-toolbar--with-filters': showFilters }">
      <!-- The count keeps its box while loading instead of unmounting: it sits
           in front of the filters now, which would jump left on every search. -->
      <p class="listing-toolbar__count" :class="{ 'is-loading': results.loading }">
        <strong class="listing-toolbar__count-value">{{ count }}</strong>
        {{ t(count === 1 ? 'projects-toolbar.result-label' : 'projects-toolbar.results-label') }}
      </p>
      <ProjectFilterBar v-if="showFilters" :regions="regions" :statuses="statuses" />
      <div class="listing-toolbar__views" role="group" :aria-label="t('listing-toolbar.views-aria')">
        <button
          v-for="option in viewOptions"
          :key="option.key"
          type="button"
          class="listing-toolbar__view"
          :class="{ 'is-active': view === option.key }"
          :aria-label="t(option.label)"
          :aria-pressed="view === option.key"
          @click="setView(option.key)"
        >
          <SvgIcon :name="option.icon" :size="16" />
          <span class="listing-toolbar__view-label">{{ t(option.label) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import ProjectFilterBar from './ProjectFilterBar.vue';
import SvgIcon from './SvgIcon.vue';
import { t } from '../lib/i18n';
import { useResults } from '../lib/useResults';
import { useScrollCollapse } from '../lib/useScrollCollapse';
import { useView } from '../lib/useView';

// A region page filters on nothing but its own region, so it mounts the row
// without a filter bar; regions and statuses are the filter bar's vocabularies.
defineProps({
  regions: { type: Array, default: () => [] },
  statuses: { type: Array, default: () => [] },
  showFilters: { type: Boolean, default: false }
});

const { results } = useResults();
const { view, setView, splitAvailable } = useView();
const { collapsed, expanding, onTransitionEnd } = useScrollCollapse();

const viewOptions = computed(() => [
  { key: 'list', icon: 'list', label: 'listing-toolbar.view.list' },
  ...(splitAvailable.value ? [{ key: 'both', icon: 'grid', label: 'listing-toolbar.view.both' }] : []),
  { key: 'map', icon: 'map', label: 'listing-toolbar.view.map' }
]);

const count = computed(() => results.meta?.total ?? 0);
</script>
